import { clearEncryptionCaches } from "apollo/encryption/caches";
import {
  useGetAccount,
  useGetCareprovider,
  useGetCareseeker,
} from "apollo/hooks/queries";
import { SafeQueryHookOptions } from "apollo/hooks/queries/utils";
import { getFetchPolicy } from "apollo/hooks/utils";
import { POST_LOGOUT_REDIRECT_URI, TRACK_EVENTS } from "core/consts";
import {
  isCareseekerLogged,
  isProviderSearchLogged,
} from "core/model/accounts";
import authApi from "core/model/api/endpoints/auth";
import { hasSSOActivatedAndConsentGiven } from "core/model/careseekers";
import { datadogLogs } from "core/model/logging";
import { getTopLevelDomain } from "core/model/utils/urls";
import {
  CareproviderConfig,
  Careseeker,
  CareseekerConfig,
  SupportedLanguage,
} from "core/types";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTracking } from "react-tracking";
import { CAREPROVIDER_CHANGED } from "reduxentities/slices/authSlice";
import { RootState } from "reduxentities/store";
import {
  selectActiveRoles,
  selectAuthType,
  selectLoggedCareproviderId,
  selectLoggedCareseeker,
  selectLoggedInAccount,
  selectPrivateKey,
  selectToken,
  selectTokenType,
} from "./selectors";
import { COUNTRY_CHANGED, LOGGED_OUT } from "./slices/authSlice";
import { LANGUAGE_CHANGED } from "./slices/settingsSlice";
import type { AppDispatch } from "./store";

// Use throughout the app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
export const usePrivateKey = () => useAppSelector(selectPrivateKey);

// Careseeker
export const useLoggedCareseeker = (
  options?: SafeQueryHookOptions<
    { careseeker: Careseeker },
    { id: number | undefined }
  >,
) => {
  const loggedCareseeker = useAppSelector(selectLoggedCareseeker);
  const [, careseeker] = useGetCareseeker(
    loggedCareseeker?.id,
    { withAdminFields: true, withProductFeatures: true, withSSO: true },
    {
      ...options,
      fetchPolicy: getFetchPolicy(options?.fetchPolicy ?? "cache-only"),
    },
  );

  return careseeker;
};

export function useLoggedCareseekerConfig<K extends keyof CareseekerConfig>(
  configProperty: K,
): CareseekerConfig[K] | undefined {
  const loggedCareseeker = useLoggedCareseeker();
  return loggedCareseeker?.config?.[configProperty];
}

export function useConnecterWithCareseeker() {
  const account = useConnecterWithLoggedAccount();
  const careseeker = useAppSelector(selectLoggedCareseeker);
  const careseekerId = careseeker?.id;
  const privateKey = usePrivateKey();
  const activeRoles = useAppSelector(selectActiveRoles);
  return {
    account,
    activeRoles,
    careseeker,
    careseekerId,
    privateKey,
  };
}
export function useIsLoggedOnClinicApp() {
  return useAppSelector(isCareseekerLogged);
}

// Careprovider
export const useLoggedCareprovider = () => {
  const careproviderId = useLoggedCareproviderId();
  const [, careprovider] = useGetCareprovider({
    id: careproviderId,
    options: { fetchPolicy: getFetchPolicy("cache-only") },
  });
  return careprovider;
};

export const useLoggedCareproviderId = () => {
  return useAppSelector(selectLoggedCareproviderId);
};

export function useLoggedCareproviderConfig<K extends keyof CareproviderConfig>(
  configProperty: K,
): CareproviderConfig[K] | undefined {
  const loggedCareprovider = useLoggedCareprovider();
  return loggedCareprovider?.config?.[configProperty];
}

export const useCareproviderChanged = () => {
  const { trackEvent } = useTracking();
  const dispatch = useAppDispatch();

  return useCallback(
    (careproviderId: number, country?: string) => {
      trackEvent({
        careprovider_id_new: careproviderId,
        name: TRACK_EVENTS.CAREPROVIDER_CHANGED,
      });
      dispatch(CAREPROVIDER_CHANGED({ careproviderId, country }));
    },
    [dispatch, trackEvent],
  );
};

// ProviderSearch
export function useIsLoggedOnProviderSearchApp() {
  return useAppSelector(isProviderSearchLogged);
}

// Account
export function useLoggedInAccount() {
  return useAppSelector(selectLoggedInAccount);
}

export function useLoggedInAccountId() {
  return useLoggedInAccount()?.id;
}

export function useConnecterWithLoggedAccount(options?: {
  cacheOnly?: boolean;
}) {
  const accountId = useLoggedInAccountId();
  const [, account] = useGetAccount(accountId || -2, {
    fetchPolicy: options?.cacheOnly ? "cache-only" : undefined,
  });
  return account;
}

// Token, auth
export function useSelectAuthType() {
  return useAppSelector(selectAuthType);
}

export function useSelectToken() {
  return useAppSelector(selectToken);
}

export function useSelectTokenType() {
  return useAppSelector(selectTokenType);
}

export function useSelectQueryToken() {
  const tokenType = useSelectTokenType();
  const token = useSelectToken();
  return tokenType === "query" ? token : undefined;
}

export const useJwtTokenData = () => {
  const tokenType = useSelectTokenType();
  const token = useSelectToken();
  const accountId = useLoggedInAccountId();

  return {
    accountId,
    token: tokenType === "jwt" ? token : undefined,
  };
};

function buildSSORedirectUrl({
  idp_logout_url,
  sso_logout_url,
}: {
  sso_logout_url?: string;
  idp_logout_url?: string;
}): string {
  if (!sso_logout_url) {
    throw new Error("SSO logout URL is required");
  }

  const createUrlWithRedirect = (urlString: string, redirectTo: string) => {
    const url = new URL(urlString);
    url.searchParams.append(POST_LOGOUT_REDIRECT_URI, redirectTo);
    return url.href;
  };

  const idpLogoutUrl = idp_logout_url?.trim();
  if (idpLogoutUrl) {
    try {
      const idpUrl = createUrlWithRedirect(
        idpLogoutUrl,
        window.location.origin,
      );
      const ssoUrl = createUrlWithRedirect(sso_logout_url, idpUrl);

      const newTab = window.open("/", "_blank");
      if (newTab) {
        newTab.focus();
      }

      return ssoUrl;
    } catch (urlError) {
      console.error("Error creating chained URL objects:", urlError);
    }
  }

  return createUrlWithRedirect(sso_logout_url, window.location.origin);
}

export function useOnLogout() {
  const { trackEvent } = useTracking();
  const dispatch = useAppDispatch();
  const loggedCareseeker = useLoggedCareseeker();

  return useCallback(
    async (options?: {
      hardRefresh?: boolean;
      tokenExpired?: boolean;
      isSSOActivation?: boolean;
    }) => {
      const isSSOActivatedAndConsentGiven =
        hasSSOActivatedAndConsentGiven(loggedCareseeker);
      let ssoRedirect;

      if (!options?.tokenExpired) {
        try {
          const response = await authApi.logout();

          // #TODO - isSSOActivatedAndConsentGiven will be migrated to a query param once the backend is updated
          if (response?.sso_logout_url && isSSOActivatedAndConsentGiven) {
            ssoRedirect = buildSSORedirectUrl(response);
          }
        } catch (e) {
          console.error(`Logout failed: ${e}`);
        }
      }

      trackEvent({
        forced: options?.hardRefresh,
        name: TRACK_EVENTS.LOGGED_OUT,
        token_expired: options?.tokenExpired,
        type: "connect",
      });

      datadogLogs.clearUser();

      clearEncryptionCaches();

      // Always dispatch LOGGED_OUT to clear the Redux store immediately
      // This ensures old tokens are properly cleared and prevents "Invalid token" errors
      // The dispatch is synchronous, so the store is cleared before any redirects
      if (options?.isSSOActivation) {
        await authApi.logout();
      }
      dispatch(LOGGED_OUT());

      if (options?.hardRefresh) {
        window.localStorage.clear();
        window.sessionStorage.clear();
        if (!ssoRedirect) {
          window.location.reload();
        }
      }

      if (ssoRedirect) {
        window.location.replace(ssoRedirect);
      }
    },
    [dispatch, trackEvent],
  );
}

// Local, language, country
export function useLocale(): SupportedLanguage {
  return useAppSelector(({ settings }) => settings?.language ?? "de");
}

export const useGetLanguage = () => {
  return useAppSelector(({ settings }) => settings?.language);
};

export const useCountry = () => {
  const country = useAppSelector(({ auth }) => auth?.country);
  return country || getTopLevelDomain();
};

export function useLanguageChange() {
  const dispatch = useAppDispatch();

  return useCallback(
    (language: SupportedLanguage) => dispatch(LANGUAGE_CHANGED(language)),
    [dispatch],
  );
}

export function useCountryChanged() {
  const dispatch = useAppDispatch();

  return useCallback(
    (country: string | undefined) => dispatch(COUNTRY_CHANGED({ country })),
    [dispatch],
  );
}
